import { mkdir, writeFile } from 'node:fs/promises';
import { dirname, join } from 'node:path';

import type { ClassificationResult } from '@/core/types/classification.js';
import { toPosixPath } from '@/core/path-utils.js';
import type { PentestReportIndex } from '@/core/types/pentest.js';
import { PATHS } from '@/core/constants/paths.js';
import { StackIntrospector } from '@/introspection/stack-introspector.js';
import { buildRetestFindings } from '@/pentest/findings.js';
import { PentestProgressTracker, runArtifactsDir, runLogsDir } from '@/pentest/progress-tracker.js';
import { buildPentestMarkdown, remediationPriorities } from '@/pentest/report-builder.js';
import {
  discoverTargetUrl,
  hashRelevantInputs,
  readJsonIfExists,
  runProjectScript,
  skillPath,
  summarizeCommandAvailability,
  toLocalTimestamp,
  toReportId,
  toolReferencePath,
  writeJson,
} from '@/pentest/shared.js';

import { buildCurrentPentestReport } from './pentest.js';

export interface PentestRetestWorkflowOptions {
  projectRoot: string;
  classification: ClassificationResult;
  sourceReportPath?: string;
  targetUrl?: string;
  focusModules?: string[];
  dbConnectionName?: string;
}

export interface PentestRetestWorkflowResult {
  run_id: string;
  report_id: string;
  report_path: string;
  sidecar_path: string;
}

const RETEST_STEPS = [
  { id: 'load-source-report', title: 'Load source pentest report' },
  { id: 'rerun-evidence', title: 'Collect fresh evidence for source findings' },
  { id: 'evaluate-source-findings', title: 'Evaluate source findings against current evidence' },
  { id: 'write-report', title: 'Write canonical pentest retest report' },
] as const;

const RETEST_SKILLS = {
  source: ['retest-verification'],
  evaluate: ['retest-verification', 'finding-normalizer'],
} as const;

export class PentestRetestWorkflow {
  private readonly introspector = new StackIntrospector();
  private readonly tracker = new PentestProgressTracker();

  async run(options: PentestRetestWorkflowOptions): Promise<PentestRetestWorkflowResult> {
    const sourceReportPath =
      options.sourceReportPath ?? extractSourceReportPath(options.classification.request_text);
    if (!sourceReportPath) {
      throw new Error('Pentest retest requires an explicit source pentest report path');
    }

    const normalizedSourcePath = normalizeSidecarPath(sourceReportPath);
    const sourceSidecar = await readJsonIfExists<PentestReportIndex>(
      join(options.projectRoot, normalizedSourcePath),
    );
    if (sourceSidecar === null) {
      throw new Error(`Source pentest sidecar not found at ${normalizedSourcePath}`);
    }

    const snapshot = await this.introspector.snapshot(options.projectRoot);
    const targetUrl =
      options.targetUrl ??
      sourceSidecar.runtime.target_url ??
      (await discoverTargetUrl(options.projectRoot, sourceSidecar.stack.primary));
    const progress = await this.tracker.ensureRun({
      projectRoot: options.projectRoot,
      workflow: 'pentest-retest',
      sourceReportPath: normalizedSourcePath,
      targetUrl,
      steps: [...RETEST_STEPS],
    });
    await this.tracker.save(options.projectRoot, progress);

    const artifactsDir = runArtifactsDir(options.projectRoot, progress.run_id);
    const logsDir = runLogsDir(options.projectRoot, progress.run_id);
    const blockedChecks: string[] = [];

    const sourceHash = await hashRelevantInputs(options.projectRoot, 'pentest-retest', {
      source_report_path: normalizedSourcePath,
      target_url: targetUrl,
    });
    if (!this.tracker.shouldSkipStep(progress, 'load-source-report', sourceHash)) {
      this.tracker.markStepRunning(progress, 'load-source-report', sourceHash);
      await this.tracker.save(options.projectRoot, progress);

      const sourceCopy = join(artifactsDir, 'source-report.json');
      await writeJson(sourceCopy, sourceSidecar);
      this.tracker.markStepCompleted(
        progress,
        'load-source-report',
        [
          toPosixPath(
            join(PATHS.PENTEST_RUNS_DIR, progress.run_id, 'artifacts', 'source-report.json'),
          ),
        ],
        [...RETEST_SKILLS.source].map(skillPath),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    const rerunHash = await hashRelevantInputs(options.projectRoot, 'pentest-retest', {
      source_report_path: normalizedSourcePath,
      target_url: targetUrl,
      step: 'rerun',
    });
    if (!this.tracker.shouldSkipStep(progress, 'rerun-evidence', rerunHash)) {
      this.tracker.markStepRunning(progress, 'rerun-evidence', rerunHash);
      await this.tracker.save(options.projectRoot, progress);

      const scriptEnv = {
        PENTEST_RUN_ID: progress.run_id,
        PENTEST_ARTIFACT_DIR: artifactsDir,
        PENTEST_TARGET_URL: targetUrl ?? '',
        PENTEST_SOURCE_REPORT: join(options.projectRoot, normalizedSourcePath),
        PENTEST_DB_CONNECTION_NAME: options.dbConnectionName ?? '',
      };

      const scriptResults = await Promise.all([
        runProjectScript(options.projectRoot, 'pentest-retest.sh', scriptEnv, logsDir),
        runProjectScript(options.projectRoot, 'pentest-audit-dependencies.sh', scriptEnv, logsDir),
        runProjectScript(options.projectRoot, 'pentest-scan-secrets.sh', scriptEnv, logsDir),
        runProjectScript(options.projectRoot, 'pentest-runtime-checks.sh', scriptEnv, logsDir),
      ]);
      blockedChecks.push(...summarizeCommandAvailability(scriptResults));

      this.tracker.markStepCompleted(
        progress,
        'rerun-evidence',
        scriptResults.flatMap((result) => result.artifact_paths),
        [...RETEST_SKILLS.evaluate].map(skillPath),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    const evaluateHash = await hashRelevantInputs(options.projectRoot, 'pentest-retest', {
      source_report_path: normalizedSourcePath,
      step: 'evaluate',
    });
    let retestReport: PentestReportIndex | null = null;
    if (!this.tracker.shouldSkipStep(progress, 'evaluate-source-findings', evaluateHash)) {
      this.tracker.markStepRunning(progress, 'evaluate-source-findings', evaluateHash);
      await this.tracker.save(options.projectRoot, progress);

      const currentReport = await buildCurrentPentestReport({
        projectRoot: options.projectRoot,
        classification: options.classification,
        snapshot,
        targetUrl,
        focusModules: options.focusModules ?? [],
        blockedChecks,
        artifactsDir,
        progressRunId: progress.run_id,
        reportTimestamp: new Date(progress.started_at),
      });
      const retestFindings = buildRetestFindings(
        sourceSidecar.findings,
        currentReport.findings,
        currentReport.runtime,
        currentReport.blocked_checks,
      );

      const timestamp = toLocalTimestamp(new Date(progress.started_at));
      const sourceSlug = normalizedSourcePath
        .replace(/^docs\/pentest\//, '')
        .replace(/\.json$/, '')
        .replace(/[^a-zA-Z0-9._-]+/g, '-');
      retestReport = {
        ...currentReport,
        report_id: toReportId('RETEST', new Date(progress.started_at)),
        workflow: 'pentest-retest',
        report_path: toPosixPath(join(PATHS.PENTEST_RETEST_DIR, `${timestamp}-${sourceSlug}.md`)),
        sidecar_path: toPosixPath(
          join(PATHS.PENTEST_RETEST_DIR, `${timestamp}-${sourceSlug}.json`),
        ),
        source_report_path: normalizedSourcePath,
        source_report_id: sourceSidecar.report_id,
        findings: retestFindings,
        next_remediation_priorities: remediationPriorities(
          retestFindings.filter((finding) => finding.retest_status !== 'fixed'),
        ),
        sources_used: [
          normalizedSourcePath,
          toolReferencePath(sourceSidecar.stack.primary),
          ...[...RETEST_SKILLS.source, ...RETEST_SKILLS.evaluate].map(skillPath),
        ],
      };

      const findingIndexPath = join(
        options.projectRoot,
        PATHS.PENTEST_RUNS_DIR,
        progress.run_id,
        'finding-index.json',
      );
      await writeJson(findingIndexPath, retestFindings);
      this.tracker.markStepCompleted(
        progress,
        'evaluate-source-findings',
        [toPosixPath(join(PATHS.PENTEST_RUNS_DIR, progress.run_id, 'finding-index.json'))],
        [...RETEST_SKILLS.evaluate].map(skillPath),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    if (retestReport === null) {
      const existing = progress.sidecar_path
        ? await readJsonIfExists<PentestReportIndex>(
            join(options.projectRoot, progress.sidecar_path),
          )
        : null;
      retestReport = existing;
    }

    if (retestReport === null) {
      throw new Error('Pentest retest did not produce a report payload');
    }

    const writeHash = await hashRelevantInputs(options.projectRoot, 'pentest-retest', {
      source_report_path: normalizedSourcePath,
      findings: retestReport.findings.map((finding) => finding.id),
    });
    if (!this.tracker.shouldSkipStep(progress, 'write-report', writeHash)) {
      this.tracker.markStepRunning(progress, 'write-report', writeHash);
      await this.tracker.save(options.projectRoot, progress);

      await writeJson(join(options.projectRoot, retestReport.sidecar_path), retestReport);
      await mkdir(dirname(join(options.projectRoot, retestReport.report_path)), {
        recursive: true,
      });
      await writeFile(
        join(options.projectRoot, retestReport.report_path),
        buildPentestMarkdown(retestReport),
      );

      this.tracker.markStepCompleted(
        progress,
        'write-report',
        [retestReport.report_path, retestReport.sidecar_path],
        [...RETEST_SKILLS.evaluate].map(skillPath),
      );
      this.tracker.markRunCompleted(
        progress,
        retestReport.report_path,
        retestReport.sidecar_path,
        retestReport.findings.map((finding) => finding.id),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    return {
      run_id: progress.run_id,
      report_id: retestReport.report_id,
      report_path: retestReport.report_path,
      sidecar_path: retestReport.sidecar_path,
    };
  }
}

function extractSourceReportPath(requestText: string): string | null {
  const match = requestText.match(/docs\/pentest\/[^\s)]+(?:\.md|\.json)/);
  return match?.[0] ?? null;
}

function normalizeSidecarPath(path: string): string {
  return path.endsWith('.md') ? path.replace(/\.md$/, '.json') : path;
}
